import { useMemo } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { type PhotoLikesMap } from '../PhotoLikesContext.tsx';
import { type useLoadPhotoLikesQuery } from './__generated__/useLoadPhotoLikesQuery.graphql.ts';

export const useLoadPhotoLikes = ({
    photoUrls,
    userId,
}: {
    photoUrls: string[];
    userId: string;
}): PhotoLikesMap => {
    const data = useLazyLoadQuery<useLoadPhotoLikesQuery>(
        graphql`
            query useLoadPhotoLikesQuery(
                $urlLabels: [String!]!
                $userId: String
                $hasUserId: Boolean!
                $shouldLoadPhotoLikes: Boolean!
            ) {
                viewer {
                    tradePhotosQuery(urlLabels: $urlLabels) @include(if: $shouldLoadPhotoLikes) {
                        edges {
                            node {
                                photo {
                                    serviceId
                                    photoLike {
                                        photoLikeId(userId: $userId) @include(if: $hasUserId)
                                        totalPhotoLikes
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
        {
            urlLabels: photoUrls,
            userId,
            hasUserId: !!userId,
            shouldLoadPhotoLikes: photoUrls.length > 0,
        },
        { fetchPolicy: 'store-or-network' }
    );

    const photoLikesMap = useMemo(() => {
        const edges = data?.viewer?.tradePhotosQuery?.edges || [];
        return edges.reduce((acc, edge) => {
            const { serviceId: photoId, photoLike } = edge?.node?.photo || {};
            if (photoId && photoLike) {
                acc.set(photoId, {
                    photoLikeId: photoLike.photoLikeId || null,
                    totalPhotoLikes: photoLike.totalPhotoLikes || 0,
                });
            }
            return acc;
        }, new Map() as PhotoLikesMap);
    }, [data]);

    return photoLikesMap;
};
